<template>
  <article
    class="mx-auto w-full"
    :class="articleClass"
    dir="rtl"
  >
    <nav class="mb-6 text-sm text-[var(--ui-text-muted)]">
      <NuxtLink
        v-if="!preview"
        to="/blog"
        class="hover:text-primary"
      >
        آموزش بیماران
      </NuxtLink>
      <span v-else>آموزش بیماران</span>
      <span class="mx-1">/</span>
      <span>{{ item.title || 'بدون عنوان' }}</span>
    </nav>

    <div
      v-if="item.coverUrl"
      class="mb-8 overflow-hidden rounded-[2rem]"
    >
      <img
        :src="item.coverUrl"
        :alt="item.title"
        class="w-full object-cover"
        :class="coverClass"
      >
    </div>

    <div
      class="grid gap-10"
      :class="gridClass"
    >
      <div class="min-w-0 space-y-8">
        <header class="space-y-4">
          <div class="flex flex-wrap gap-2 text-xs text-[var(--ui-text-muted)]">
            <NuxtLink
              v-if="item.category && !preview"
              :to="`/blog/category/${item.category.slug}`"
              class="rounded-full bg-primary/10 px-2.5 py-1 text-primary"
            >
              {{ item.category.title }}
            </NuxtLink>
            <span
              v-else-if="item.category"
              class="rounded-full bg-primary/10 px-2.5 py-1 text-primary"
            >
              {{ item.category.title }}
            </span>
            <span v-if="item.publishedAt">{{ faDate(item.publishedAt, 'short') }}</span>
            <span v-else-if="preview">پیش‌نویس</span>
            <span>{{ toFaDigits(readingMinutes) }} دقیقه مطالعه</span>
          </div>
          <h1 :class="titleClass">
            {{ item.title || 'بدون عنوان' }}
          </h1>
          <p
            v-if="item.excerpt"
            class="leading-8 text-[var(--ui-text-muted)]"
            :class="excerptClass"
          >
            {{ item.excerpt }}
          </p>
          <div class="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--ui-bg-muted)] px-4 py-3">
            <div>
              <p class="font-semibold">
                {{ item.authorName || 'پزشک' }}
              </p>
              <p
                v-if="item.authorTitle"
                class="text-xs text-[var(--ui-text-muted)]"
              >
                {{ item.authorTitle }}
              </p>
            </div>
            <p
              v-if="item.reviewerName"
              class="text-xs text-[var(--ui-text-muted)]"
            >
              بازبینی علمی: {{ item.reviewerName }}
              <span v-if="item.reviewedAt"> · {{ faDate(item.reviewedAt, 'short') }}</span>
            </p>
          </div>
        </header>

        <details
          v-if="outline.length && showMobileOutline"
          class="rounded-2xl border border-[var(--ui-border)] p-4"
          :class="mobileOutlineClass"
        >
          <summary class="cursor-pointer font-medium">
            فهرست مطالب
          </summary>
          <ol class="mt-3 space-y-2 text-sm">
            <li
              v-for="heading in outline"
              :key="heading.id"
            >
              <a
                :href="`#${heading.id}`"
                @click="onOutlineClick(heading.id, $event)"
              >{{ heading.text }}</a>
            </li>
          </ol>
        </details>

        <div class="space-y-8">
          <BlogLeadGate
            v-if="item.leadGate && item.leadGate !== 'none'"
            :content="content"
            :percentage="item.gateAtPercentage"
            :mode="item.leadGate"
            :slug="item.slug"
            :device="resolvedDevice"
            :preview="preview"
          />
          <template v-else>
            <BlogBlockView
              v-for="block in content"
              :key="block.id"
              :block="block"
              :device="resolvedDevice"
              :preview="preview"
            />
          </template>
        </div>

        <div
          v-if="tags.length"
          class="flex flex-wrap gap-2"
        >
          <template v-if="preview">
            <span
              v-for="tag in tags"
              :key="tag"
              class="rounded-full bg-[var(--ui-bg-muted)] px-3 py-1 text-xs"
            >
              {{ tag }}
            </span>
          </template>
          <template v-else>
            <NuxtLink
              v-for="tag in tags"
              :key="tag"
              :to="`/blog?tag=${encodeURIComponent(tag)}`"
              class="rounded-full bg-[var(--ui-bg-muted)] px-3 py-1 text-xs"
            >
              {{ tag }}
            </NuxtLink>
          </template>
        </div>

        <slot name="after-content">
          <div
            v-if="preview"
            class="rounded-2xl border border-dashed border-[var(--ui-border)] px-4 py-6 text-sm text-[var(--ui-text-muted)]"
          >
            دیدگاه بیماران در سایت عمومی، زیر همین مقاله نمایش داده می‌شود.
          </div>
        </slot>
      </div>

      <aside
        v-if="showDesktopSidebar"
        class="min-w-0"
        :class="desktopAsideClass"
      >
        <div class="space-y-6" :class="sidebarStickyClass">
          <div
            v-if="outline.length"
            class="rounded-2xl border border-[var(--ui-border)] p-4"
          >
            <p class="mb-3 text-sm font-semibold">
              فهرست مطالب
            </p>
            <ol class="space-y-2 text-sm">
              <li
                v-for="heading in outline"
                :key="heading.id"
              >
                <a
                  :href="`#${heading.id}`"
                  class="text-[var(--ui-text-muted)] hover:text-primary"
                  @click="onOutlineClick(heading.id, $event)"
                >{{ heading.text }}</a>
              </li>
            </ol>
          </div>
          <slot name="sidebar" />
        </div>
      </aside>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { BlogPostPublic, BlogPreviewDevice } from '~/types/blog'
import { headingOutline } from '~/utils/blog/plainText'
import { readingMinutesFromBlocks } from '~/utils/blog/readingTime'
import { faDate } from '~/utils/faDate'
import { toFaDigits } from '~/utils/faDigits'

const props = withDefaults(defineProps<{
  item: BlogPostPublic
  device?: BlogPreviewDevice | 'auto'
  preview?: boolean
}>(), {
  device: 'auto',
  preview: false,
})

const content = computed(() => props.item.content ?? [])
const tags = computed(() => props.item.tags ?? [])
const outline = computed(() => headingOutline(content.value))
const readingMinutes = computed(() => (
  props.preview ? readingMinutesFromBlocks(content.value) : (props.item.readingMinutes || 1)
))
const resolvedDevice = computed<BlogPreviewDevice | undefined>(() => (
  props.device === 'auto' ? undefined : props.device
))

const showDesktopSidebar = computed(() => {
  if (props.device === 'auto') return true
  return props.device === 'desktop'
})

const showMobileOutline = computed(() => {
  if (props.device === 'auto') return true
  return props.device !== 'desktop'
})

const articleClass = computed(() => {
  if (props.device === 'mobile') return 'max-w-none px-4 py-6'
  if (props.device === 'tablet') return 'max-w-none px-5 py-8'
  if (props.device === 'desktop') return 'max-w-6xl px-6 py-10 lg:px-8'
  return 'max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12'
})

const coverClass = computed(() => {
  if (props.device === 'mobile') return 'aspect-[16/10]'
  return 'aspect-[16/8]'
})

const gridClass = computed(() => {
  if (props.device === 'desktop') return 'grid-cols-[minmax(0,1fr)_16rem]'
  if (props.device === 'auto') return 'lg:grid-cols-[minmax(0,1fr)_16rem]'
  return ''
})

const titleClass = computed(() => {
  if (props.device === 'mobile') return 'text-2xl font-bold leading-tight'
  if (props.device === 'tablet') return 'text-3xl font-bold leading-tight'
  return 'text-3xl font-bold leading-tight sm:text-4xl'
})

const excerptClass = computed(() => (
  props.device === 'mobile' ? 'text-base' : 'text-lg'
))

const sidebarStickyClass = computed(() => (
  props.preview ? 'sticky top-4' : 'sticky top-24'
))

const mobileOutlineClass = computed(() => (
  props.device === 'auto' ? 'lg:hidden' : ''
))

const desktopAsideClass = computed(() => (
  props.device === 'auto' ? 'hidden lg:block' : ''
))

function onOutlineClick(id: string, event: Event) {
  if (!props.preview) return
  event.preventDefault()
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

</script>
